import axios from 'axios';
import config from '../config';
import { kilometersToMeters } from './converter';
import { sanitizePathSegment } from './security';

const evURL = config.evProfiles.url;

const getAuthHeaders = (oAuth, apiKey) => {
  if (apiKey) {
    return {};
  }
  if (oAuth?.accessToken) {
    return {
      headers: {
        Authorization: `Bearer ${oAuth.accessToken}`,
      },
    };
  }
  return {};
};

export class EVAuthError extends Error {
  constructor() {
    super('EV API authentication failed');
    this.name = 'EVAuthError';
  }
}

export const fetchEVMakes = async (oAuth, apiKey) => {
  try {
    const url = apiKey ? `${evURL}/makes?apiKey=${apiKey}` : `${evURL}/makes`;
    const response = await axios.get(url, getAuthHeaders(oAuth, apiKey));
    return response.data?.items || [];
  } catch (error) {
    if (apiKey) {
      throw new EVAuthError();
    }
    return [];
  }
};

export const fetchEVModels = async (makeId, oAuth, apiKey) => {
  try {
    const safeMakeId = sanitizePathSegment(makeId);
    if (!safeMakeId) return [];
    const url = `${evURL}/makes/${safeMakeId}/models`;
    const response = await axios.get(apiKey ? `${url}?apiKey=${apiKey}` : url, getAuthHeaders(oAuth, apiKey));
    return response.data?.items || [];
  } catch (error) {
    console.error(`Error fetching models for ${makeId}:`, error);
    return [];
  }
};

export const fetchEVVariants = async (makeId, modelId, oAuth, apiKey) => {
  try {
    const safeMakeId = sanitizePathSegment(makeId);
    const safeModelId = sanitizePathSegment(modelId);
    if (!safeMakeId || !safeModelId) return [];
    const url = `${evURL}/makes/${safeMakeId}/models/${safeModelId}/variants`;
    const response = await axios.get(apiKey ? `${url}?apiKey=${apiKey}` : url, getAuthHeaders(oAuth, apiKey));
    return response.data?.items || [];
  } catch (error) {
    console.error(`Error fetching variants for ${makeId}/${modelId}:`, error);
    return [];
  }
};

export const fetchEVProfile = async (profileId, oAuth, apiKey) => {
  try {
    const safeProfileId = sanitizePathSegment(String(profileId));
    if (!safeProfileId) return null;
    const url = `${evURL}/profiles?profileIds=${safeProfileId}`;
    const response = await axios.get(apiKey ? `${url}&apiKey=${apiKey}` : url, getAuthHeaders(oAuth, apiKey));
    const items = response.data?.items || response.data?.profiles || [];
    return items[0] || null;
  } catch (error) {
    console.error(`Error fetching EV profile ${profileId}:`, error);
    return null;
  }
};

export const calculateMaxDistance = (rangeKm, safetyBufferPercent = 20) => {
  if (!rangeKm || rangeKm <= 0) return 0;

  const rangeMeters = kilometersToMeters(rangeKm);
  const safetyFactor = 1 - safetyBufferPercent / 100;

  return Math.round(rangeMeters * safetyFactor);
};

export const getEVProfileName = (make, model, variant) => {
  if (!make && !model && !variant) return 'EV';
  return [make, model, variant].filter(Boolean).join(' ');
};
